import React, { useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  BaseEdge,
  getSmoothStepPath,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import "../../styles/philosophyFlow.css";

const nodeStyle = {
  borderRadius: "999px",
  height: 360,
  width: 360,
  padding: "14px 20px", // bigger
  fontWeight: 700,
  fontSize: "42px",
  border: "2px solid rgba(65,105,178,0.5)",
  background: "rgba(255,255,255,0.85)",
  color: "#183153",

  display: "flex",              // 🔑
  alignItems: "center",         // vertical center
  justifyContent: "center",     // horizontal center
  textAlign: "center",          // center multiline text
};

const AnimatedSkillEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: data?.isActive
            ? "rgba(47,74,145,0.95)"
            : "rgba(159,125,54,0.35)",
          strokeWidth: data?.isActive ? 3 : 2,
        }}
      />

      {data?.isActive && (
        <circle r="5" fill="rgba(47,74,145,0.95)">
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            path={edgePath}
          />
        </circle>
      )}
    </>
  );
};

const edgeTypes = {
  animatedSkill: AnimatedSkillEdge,
};


const branchTopics = {
  epistemology: ["Rationalism", "Empiricism", "Skepticism", "Pragmatism"],
  ethics: ["Virtue Ethics", "Deontology", "Consequentialism", "Metaethics"],
  logic: ["Aristotelian Logic", "Symbolic Logic", "Modal Logic", "Dialectics"],
  metaphysics: ["Ontology", "Materialism", "Idealism", "Free Will"],
  aesthetics: ["Beauty", "Sublime", "Taste", "Art Theory"],
  political: ["Liberalism", "Marxism", "Anarchism", "Critical Theory"],
};

const topicStyle = {
  ...nodeStyle,
  padding: "10px 14px",
  fontSize: "42px",
  borderRadius: "999px",
  opacity: 0.95,
};

const nodes = [
  {
    id: "core",
    position: { x: 498, y: -10 },
    data: { label: "Philosophy" },
    style: {
      ...nodeStyle,
      width: 360,
      height: 360,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      fontSize: "42px",
    },
  },

  { id: "epistemology", position: { x: 1500, y: 400 }, data: { label: "Epistemology" }, style: nodeStyle },
  { id: "ethics", position: { x: 1100, y: 400 }, data: { label: "Ethics" }, style: nodeStyle },
  { id: "logic", position: { x: 700, y: 400 }, data: { label: "Logic" }, style: nodeStyle },
  { id: "metaphysics", position: { x: 300, y: 400 }, data: { label: "Metaphysics" }, style: nodeStyle },
  { id: "aesthetics", position: { x: -100, y: 400 }, data: { label: "Aesthetics" }, style: nodeStyle },
  { id: "political", position: { x: -500, y: 400 }, data: { label: "Political" }, style: nodeStyle },

  ...Object.entries(branchTopics).flatMap(([branchId, topics]) => {
    const parentNode = {
      epistemology: { x: 1540, y: 700 },
      ethics: { x: 1140, y: 700 },
      logic: { x: 740, y: 700 },
      metaphysics: { x: 340, y: 700 },
      aesthetics: { x: -60, y: 700 },
      political: { x: -460, y: 700 },
    }[branchId];

    return topics.map((topic, index) => ({
      id: `${branchId}-${topic.toLowerCase().replaceAll(" ", "-")}`,
      position: {
        x: parentNode.x - 40,
        y: parentNode.y + 140 + index * 440
      },
      data: { label: topic },
      style: topicStyle,
    }));
  }),
];

const PhilosophyFlow = () => {
  const [activeNode, setActiveNode] = useState(null);

  const branchEdges = [
  { id: "e1", type: "animatedSkill", source: "core", target: "epistemology" },
  { id: "e2", type: "animatedSkill", source: "core", target: "ethics" },
  { id: "e3", type: "animatedSkill", source: "core", target: "logic" },
  { id: "e4", type: "animatedSkill", source: "core", target: "metaphysics" },
  { id: "e5", type: "animatedSkill", source: "core", target: "aesthetics" },
  { id: "e6", type: "animatedSkill", source: "core", target: "political" },
];

const topicEdges = Object.entries(branchTopics).flatMap(([branchId, topics]) =>
  topics.map((topic) => ({
    id: `${branchId}-${topic.toLowerCase().replaceAll(" ", "-")}-edge`,
    type: "animatedSkill",
    source: branchId,
    target: `${branchId}-${topic.toLowerCase().replaceAll(" ", "-")}`,
  }))
);

const edges = [...branchEdges, ...topicEdges].map((edge) => ({
  ...edge,
  data: {
    isActive:
      activeNode === edge.target
  },
}));
  
  return (
    <div className="flowWrapper">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodeClick={(event, node) => {
          if (node.id === "core") return; // ignore center
          setActiveNode(node.id);
        }}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default PhilosophyFlow;